import { FC, SetStateAction } from 'react';
import { Box, Flex, StyledFormSelect, StyledInput, StyledDatePicker, WrappedQuery } from '.';
import { formatSelectValue } from '../../utils';

type FilterOption = {
  label: string;
  value: string;
  isText?: boolean;
  isNumber?: boolean;
  isDate?: boolean;
  options?: { label: string; value: string | boolean }[];
  query?: { query: any; result: string; variables: any };
};

type ChangeHandlerArgs = {
  value: string | Date | number | null;
};

type Props = {
  changeHandler: (args: ChangeHandlerArgs, stateKey?: string) => void;
  filterOptions: FilterOption[];
  filterState: SetStateAction<any>;
  searchName?: string;
  setSearchName?: (data: string) => void;
};

/**
 * This component is intended to be pure and extensible for use
 * in all the instances where we need to add filters
 * @param param0
 */

const MobileFilters: FC<Props> = ({ changeHandler, filterOptions, filterState, searchName, setSearchName }) => {
  const filterType = filterState.filterType;
  const [filterOption] = filterOptions.filter(({ value }) => value === filterType);

  return (
    <Flex size="1" direction="column" width="100%" zIndex="50">
      <Flex size="0.25" direction="row">
        <StyledFormSelect
          isClearable={true}
          name="filterOptions"
          aria-label="Eg ECD Registration"
          onChange={(option: any) => changeHandler(option, 'filterType')}
          options={filterOptions.map(({ value, label }: { value: string; label: string }) => ({ value, label }))}
          placeholder="Eg. ECD Registration"
          mb="0"
        />
      </Flex>
      {filterOption && filterOption.query && (
        <Flex size="0.25" direction="row" marginRight="10px">
          <WrappedQuery query={filterOption.query.query} variables={filterOption.query.variables}>
            {({ loading, error, data, fetchMore }) => (
              <StyledFormSelect
                aria-label="Refine"
                autoFocus={true}
                inputValue={searchName}
                isClearable={true}
                name="refine"
                mb="0"
                onChange={option => changeHandler(option, filterOption.value)}
                options={(() => {
                  if (loading || error) {
                    return [];
                  }

                  if (filterOption.query && data[filterOption.query.result].edges) {
                    return data[filterOption.query.result].edges.map((item: any) =>
                      formatSelectValue({
                        ...((item.node.firstName || item.node.lastName) && {
                          label: `${item.node.firstName} ${item.node.lastName}`,
                        }),
                        ...(item.node.localMunicipalityShorthand && { label: item.node.localMunicipalityShorthand }),
                        ...(item.node.name && { label: item.node.name }),
                        value: item.node.id,
                      })
                    );
                  }

                  return (
                    filterOption.query &&
                    data[filterOption.query.result].map((item: any) =>
                      formatSelectValue({
                        ...((item.firstName || item.lastName) && { label: `${item.firstName} ${item.lastName}` }),
                        ...(item.localMunicipalityShorthand && { label: item.localMunicipalityShorthand }),
                        ...(item.name && { label: item.name }),
                        value: item.id,
                      })
                    )
                  );
                })()}
                placeholder="Select option"
                setSearchName={setSearchName}
                scrollHandler={async () => {
                  if (filterOption.query) {
                    // call the next page of the circuits query
                    return (
                      data[filterOption.query.result] &&
                      data[filterOption.query.result].pageInfo &&
                      data[filterOption.query.result].pageInfo.hasNextPage &&
                      fetchMore({
                        variables: {
                          input: {
                            ...(filterOption.query.variables &&
                              filterOption.query.variables.input && { ...filterOption.query.variables.input }),
                            after:
                              data[filterOption.query.result] &&
                              data[filterOption.query.result].pageInfo &&
                              data[filterOption.query.result].pageInfo.endCursor,
                          },
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                          if (filterOption.query) {
                            const newResults = fetchMoreResult[filterOption.query.result].edges;

                            return {
                              [filterOption.query.result]: {
                                aggregate: fetchMoreResult[filterOption.query.result].aggregate,
                                pageInfo: fetchMoreResult[filterOption.query.result].pageInfo,
                                edges: [...previousResult[filterOption.query.result].edges, ...newResults],
                                __typename: fetchMoreResult[filterOption.query.result].__typename,
                              },
                            };
                          }
                          return;
                        },
                      })
                    );
                  }
                  return false;
                }}
              />
            )}
          </WrappedQuery>
        </Flex>
      )}
      {filterOption && filterOption.options && (
        <Flex size="0.25" direction="row" marginRight="10px">
          <StyledFormSelect
            isClearable={true}
            name="refine"
            aria-label="Refine"
            onChange={option => changeHandler(option, filterOption.value)}
            options={filterOption.options}
            placeholder="Select ..."
            mb="0"
          />
        </Flex>
      )}
      {filterOption && (filterOption.isText || filterOption.isNumber) && (
        <Flex size="0.25" direction="row" marginRight="10px">
          <Box width="100%">
            <StyledInput
              type={filterOption.isNumber ? 'number' : 'text'}
              id="searchTerm"
              aria-label="Search"
              onChange={(event: { target: { value: string | number } }) =>
                changeHandler({ value: event.target.value }, filterOption.value)
              }
              placeholder="Search..."
              px={2}
            />
          </Box>
        </Flex>
      )}
      {filterOption && filterOption.isDate && (
        <Flex size="0.25" direction="row" marginRight="10px">
          <Box width="100%">
            <StyledDatePicker
              aria-label="Date"
              name="dateSearch"
              isSingleDate={true}
              setStartDate={(startDate: Date | null) => changeHandler({ value: startDate }, filterOption.value)}
              setEndDate={() => null}
              startDate={filterState[filterType]}
              px={2}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

export default MobileFilters;
